import React, { useState, useEffect, useRef } from "react";
import {
  Building, FileText, MapPin, Globe, Plug, HelpCircle, Sparkles, PackageCheck, PackageX, FileSignature, Receipt, Coins, HandCoins, Truck, Webhook, MailCheck, Route, PlugZap,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import useAuth from "../../../hooks/useAuth";
import useUrlState from "../../../hooks/useUrlState";
import { getLogisticsCompany } from "../../../api/settings";
import OrganizacionSection from "./sections/OrganizacionSection";
import WhiteLabelSection from "./sections/WhiteLabelSection";
import DriverPaymentSection from "./sections/DriverPaymentSection";
import CommercePricingSection from "./sections/CommercePricingSection";
import CodSection from "./sections/CodSection";
import DriverContractsSection from "./sections/DriverContractsSection";
import DriverPaymentNotificationsSection from "./sections/DriverPaymentNotificationsSection";
import ProposalsTab from "./tabs/ProposalsTab";
import LocationsTab from "./tabs/LocationsTab";
import CoverageTab from "./tabs/CoverageTab";
import IntegrationsTab from "./tabs/IntegrationsTab";
import WebhooksSection from "./sections/WebhooksSection";
import InvoicingSection from "./sections/invoicing/InvoicingSection";
import BsaleSection from "./sections/BsaleSection";
import DeliveryFormConfig from "../../../components/deliveryConfig/DeliveryFormConfig";
import DriverWorkflowConfig from "../../../components/deliveryConfig/DriverWorkflowConfig";
import RouteRemovalPolicyConfig from "../../../components/deliveryConfig/RouteRemovalPolicyConfig";
import DriverAppDisplayConfig from "../../../components/deliveryConfig/DriverAppDisplayConfig";
import DeliveryFailedFormConfig from "../../../components/deliveryConfig/DeliveryFailedFormConfig";
import PickupFormConfig from "../../../components/deliveryConfig/PickupFormConfig";
import RemitoRecipientsConfig from "../../../components/deliveryConfig/RemitoRecipientsConfig";
import Faqs from "../faqs";
import "./AdminSettings.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Navegación agrupada — estructura del front nuevo (/configuracion).
// ─────────────────────────────────────────────────────────────────────────────

const NAV = [
  {
    label: "General",
    items: [
      { id: "organizacion", label: "Organización", icon: Building, description: "Datos legales y configuración regional de tu empresa." },
      { id: "whitelabel", label: "White label", icon: Sparkles, description: "Logo y elementos de marca visibles para tus comercios y clientes." },
      { id: "proposals", label: "Propuestas de valor", icon: FileText, description: "Gestiona los servicios que ofreces a tus clientes." },
      { id: "locations", label: "Ubicaciones", icon: MapPin, description: "Administra las instalaciones y geocercas de tu empresa." },
      { id: "coverage", label: "Cobertura", icon: Globe, description: "Define las regiones y comunas donde operas." },
      { id: "drivers", label: "Repartidores", icon: Route, description: "Reglas de cómo trabajan tus repartidores en la app: si deben entregar siguiendo su ruta, si pueden quitar pedidos de ella y qué ven de cada pedido." },
      { id: "delivery-form", label: "Evidencia de entrega", icon: PackageCheck, description: "Datos que el repartidor debe registrar de quien recibe el paquete, según el tipo de paquete, y el aviso por distancia al destino. La foto de evidencia se pide siempre." },
      { id: "delivery-failed", label: "Entrega fallida", icon: PackageX, description: "Datos que el repartidor debe registrar cuando una entrega falla, según el motivo." },
      { id: "pickup-form", label: "Retiros y recepción", icon: Truck, description: "Datos de quien entrega los paquetes al cerrar un retiro o una recepción en bodega, y a quién se le envía el remito por correo." },
      { id: "commerce-pricing", label: "Cobro a comercios", icon: Receipt, description: "Define cuánto le cobras a tus comercios por pedido: precio base, tarifas por comuna y recargo por sobredimensionado." },
      { id: "driver-payment", label: "Pago a repartidores", icon: Coins, description: "Define el monto a pagar por entrega según la comuna de destino y el sobredimensionado del paquete." },
      { id: "driver-payment-notifications", label: "Avisos de pago a repartidores", icon: MailCheck, description: "Qué avisos por correo recibe tu equipo sobre el pago a repartidores y a quiénes les llegan." },
      { id: "cod", label: "Pago contra entrega", icon: HandCoins, description: "Define si tus comercios pueden cobrar el pedido en la entrega, con qué medios de pago y qué evidencia debe registrar el repartidor." },
      { id: "contratos", label: "Contratos de repartidores", icon: FileSignature, description: "Sube y versiona el contrato de prestación de servicios que tus repartidores firman desde la app." },
    ],
  },
  {
    label: "Integraciones",
    items: [
      { id: "integrations", label: "Integraciones", icon: Plug, description: "Conecta plataformas e-commerce y canales de comunicación." },
      { id: "webhooks", label: "Webhooks", icon: Webhook, description: "Recibe en tu sistema cada cambio de estado de tus envíos, sin tener que consultarnos." },
      { id: "invoicing", label: "Facturación", icon: Receipt, description: "Conecta con el SII (Chile) o ARCA (Argentina) y emite la factura de tus notas de cobro." },
      { id: "bsale", label: "Bsale", icon: PlugZap, description: "Conecta tu cuenta Bsale para facturar a tus comercios con tu propia facturación electrónica (solo Chile)." },
    ],
  },
  {
    label: "Soporte",
    items: [
      { id: "faqs", label: "Preguntas frecuentes", icon: HelpCircle, description: "Administra las preguntas frecuentes que verán tus clientes.", embed: true },
    ],
  },
];

const ALL_ITEMS = NAV.flatMap((s) => s.items);

export default function AdminSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Sección activa persistida en la URL (?tab=) con push ⇒ el back del navegador
  // alterna entre secciones. Sin el param ⇒ "organizacion" (idéntico al actual).
  const [activeTab, setActiveTab] = useUrlState("tab", "organizacion", { push: true });
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const contentRef = useRef(null);
  const activePillRef = useRef(null);

  const companyId = user?.logisticCompanyId;

  useEffect(() => {
    // Sin empresa asociada no hay nada que cargar: hay que apagar el flag igual, o las
    // secciones que lo consumen se quedan en "cargando" para siempre.
    if (!companyId) {
      setLoadingCompany(false);
      return;
    }
    setLoadingCompany(true);
    getLogisticsCompany(companyId)
      .then((res) => setCompany(res.data ?? res))
      .catch(() => setCompany(null))
      .finally(() => setLoadingCompany(false));
  }, [companyId]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  // Un ?tab= inválido (link viejo / typo) no debe dejar el panel en blanco:
  // validamos contra el catálogo real de secciones y caemos a "organizacion".
  const safeTab = ALL_ITEMS.some((it) => it.id === activeTab) ? activeTab : "organizacion";
  const active = ALL_ITEMS.find((it) => it.id === safeTab);

  // Con deep-link (?tab=…) el pill activo de la fila mobile puede quedar fuera
  // de pantalla: lo centramos al montar y en cada cambio de sección.
  useEffect(() => {
    const el = activePillRef.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({
      inline: "center",
      block: "nearest",
      behavior: reduce ? "auto" : "smooth",
    });
  }, [safeTab]);

  const renderPanel = () => {
    // `loadingCompany` distingue "el doc todavía no llegó" de "llegó vacío o falló": las
    // secciones que derivan estado del documento (p. ej. la cobertura del editor de
    // tarifas por comuna) no pueden confundir ambos casos.
    const props = { company, companyId, loadingCompany, onCompanyUpdate: setCompany };
    switch (safeTab) {
      case "organizacion": return <OrganizacionSection {...props} />;
      case "whitelabel": return <WhiteLabelSection {...props} />;
      case "proposals": return <ProposalsTab {...props} />;
      case "locations": return <LocationsTab {...props} />;
      case "coverage": return <CoverageTab {...props} />;
      // Reglas de trabajo del repartidor en la app. Pestaña propia para que se encuentre:
      // dentro de "Evidencia de entrega" nadie la buscaba. Tres configs autocontenidas:
      // la ruta obligatoria y lo que muestra la app son secciones de DeliveryConfig; la
      // política para quitar pedidos de la ruta vive en microservice-packages.
      case "drivers":
        return (
          <>
            <DriverWorkflowConfig />
            <div className="mt-8">
              <RouteRemovalPolicyConfig />
            </div>
            <div className="mt-8">
              <DriverAppDisplayConfig />
            </div>
          </>
        );
      case "delivery-form": return <DeliveryFormConfig />;
      case "delivery-failed": return <DeliveryFailedFormConfig />;
      case "pickup-form":
        // Dos configs autocontenidas en la misma pestaña: cada una carga y guarda
        // su propia sección de DeliveryConfig (formulario de colecta / remito).
        return (
          <>
            <PickupFormConfig />
            <div className="mt-8">
              <RemitoRecipientsConfig />
            </div>
          </>
        );
      case "commerce-pricing": return <CommercePricingSection {...props} />;
      case "driver-payment": return <DriverPaymentSection {...props} />;
      // Sin `props`: el alcance lo impone billing desde el JWT.
      case "driver-payment-notifications": return <DriverPaymentNotificationsSection />;
      case "cod": return <CodSection {...props} />;
      case "contratos": return <DriverContractsSection {...props} />;
      case "integrations": return <IntegrationsTab {...props} />;
      // No recibe `props`: el alcance lo impone el backend desde el JWT, así que
      // la sección no necesita saber de qué empresa es.
      case "webhooks": return <WebhooksSection />;
      case "invoicing": return <InvoicingSection {...props} />;
      case "bsale": return <BsaleSection {...props} />;
      case "faqs": return <Faqs />;
      default: return null;
    }
  };

  return (
    <div className="as-root">
      {/* Sin PageHeader: el header global ya muestra "Configuración de empresa".
          La empresa activa se conserva como kicker discreto, y cada pestaña
          adentro tiene su propio H2 + descripción. */}
      {!loadingCompany && company?.name && (
        <div className="as-company-kicker">{company.name}</div>
      )}

      {/* ── Layout: sub-nav agrupado + contenido ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 lg:gap-6 items-start">
        {/* MOBILE/TABLET (<lg): fila de pills con scroll horizontal — sin
            agrupaciones para no saturar la vista. */}
        <nav
          className="lg:hidden -mx-4 px-4 overflow-x-auto"
          aria-label={t("Secciones de configuración")}
        >
          <ul className="flex items-center gap-2 min-w-max pb-1">
            {ALL_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = item.id === safeTab;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    ref={isActive ? activePillRef : undefined}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => handleTabChange(item.id)}
                    className={
                      "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium whitespace-nowrap border transition-colors " +
                      (isActive
                        ? "bg-brand-50 border-brand-500/40 text-brand-700"
                        : "bg-bg border-border text-fg-muted hover:text-fg")
                    }
                  >
                    <Icon className={"w-3.5 h-3.5 shrink-0 " + (isActive ? "text-brand-600" : "text-fg-subtle")} aria-hidden="true" />
                    {t(item.label)}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* DESKTOP (lg+): aside agrupado con sticky */}
        <aside className="hidden lg:block lg:sticky lg:top-4 lg:self-start">
          <nav className="bg-bg-muted border border-border rounded-xl p-2" aria-label={t("Secciones de configuración")}>
            {NAV.map((section) => (
              <div key={section.label} className="mb-3 last:mb-0">
                <div className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
                  {t(section.label)}
                </div>
                <ul className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.id === safeTab;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          aria-current={isActive ? "page" : undefined}
                          onClick={() => handleTabChange(item.id)}
                          className={
                            "w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors " +
                            (isActive
                              ? "bg-brand-50 text-brand-700 font-medium"
                              : "text-fg-muted hover:bg-bg-subtle hover:text-fg")
                          }
                        >
                          <Icon className={"w-4 h-4 shrink-0 " + (isActive ? "text-brand-600" : "text-fg-subtle")} aria-hidden="true" />
                          <span className="truncate">{t(item.label)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        <div ref={contentRef} className="as-content min-w-0">
          {active && !active.embed && (
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-fg">{t(active.label)}</h2>
              <p className="text-sm text-fg-muted mt-0.5">{t(active.description)}</p>
            </div>
          )}
          <div key={activeTab} className="as-panel__content--animated">
            {renderPanel()}
          </div>
        </div>
      </div>
    </div>
  );
}
